import {
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	forwardRef,
	OnDestroy,
	HostBinding,
	Self,
	Optional
} from "@angular/core";
import { ControlValueAccessor, NgControl } from "@angular/forms";
import { IdService } from "../shared/services/id.service";
import { formatNumber } from "../shared/utils/formatting";
import { langTypes } from "../shared/types/common";
@Component({
	selector: "rbc-input",
	templateUrl: "./input.component.html",
	styles: []
})
export class InputComponent implements ControlValueAccessor, OnDestroy, OnInit {
	// Private Members
	defaultError = "Invalid";
	focused = false;
	statusMessage = null;
	typeOverwrite;
	// Allow the input to be disabled, and when it is make it somewhat transparent.
	@Input() id: string;
	@Input() placeholder = "";
	@Input() label: string;
	@Input() name: string;
	@Input() helpText: string;
	@Input() unit: string;
	@Input('value') _value;
	@Input() ariaAttributes: object;
	@Input() validateRegex: string;
	@Input() size: "small" | "medium" | "large" = "medium";
	@Input() sizeWidth: "small" | "medium" | "large" | "auto";
	@Input() type:
		| "text"
		| "password"
		| "email"
		| "tel"
		| "search"
		| "url"
		| "number"
		| "date"
		| "month"
		| "week"
		| "time"
		| "file"
		| "postalcode"
		| "zipcode" = "text";
	@Input() search: boolean | Function;
	@Input() secure = false;
	@Input() required = false;
	@Input() showRequired: boolean;
	@Input() noWidth = false;
	@Input() disabled = false;
	@Input() charCount: string | number;
	@Input() unitPositionX: "left" | "right" = "right";
	@Input() formatNumberLive = false;
	@Input() formatLang: langTypes = "en";
	@Input() displayError = true;
	@Input() displayErrorColor = true;
	@Input() status: boolean | string;
	@Input() statusType: "danger" | "success" | "warning" | "info";
	@Input() validateHtml: boolean;
	@Input() labelSrOnly;
	@Input() helpTextSrOnly;
	// Aria a11y attributes
	@Input() ariaAutoComplete: string;
	@Input() ariaInvalid: boolean;
	@Input() ariaControls: string;
	@Input() ariaActiveDesendant: string;
	@Input() ariaLabel: string;
	@Input() ariaExpanded: string;
	@Input() ariaOwns: string;
	@Input() ariaHasPopup: string;
	@Input() ariaDescribedBy: string;
	@Input() ariaLabelledBy: string;
	@Input() accept: string;
	@Input() clearLabel = "Clear";
	@Input() role: string;
	@Input() min: string | number;
	@Input() step: string | number;

	@Output() clear = new EventEmitter<string>();
	@Output() onKeyDown = new EventEmitter<string>();
	@Output() focusIn = new EventEmitter<any>();
	@Output() focusOut = new EventEmitter<any>();
	@Output() blur = new EventEmitter<any>();
	@Output() onInput = new EventEmitter<string>();
	@Output() inputTextUpdate = new EventEmitter<any>();
	@Output() statusUpdate = new EventEmitter<any>();
	statusTypeComputed = this.statusType;

	get value(){
		return this._value;
	}

	set value(val){
		if (val !== undefined && this._value !== val) {
			this._value = val
			this.onChange(val);
		}
	}

	// @HostBinding("class") get hostClasses() {
	// 	return this.classGen();
	// }

	// â¬ï¸ seems to be causing the app level class attribute values to be overwritten
	// and this is a temporary fix for that issue:
	@HostBinding("class.rbc-input") inputClass = true;
	@HostBinding("class.rbc-input-unit-wrap") get inputUnitClass() {
		return this.unit;
	}
	@HostBinding("class.rbc-input-width-none") get inputNoWidthClass() {
		return this.noWidth;
	}
	@HostBinding("class.rbc-input-small") get inputSizeSmallClass() {
		return this.size === 'small';
	}
	@HostBinding("class.rbc-input-medium") get inputSizeMediumClass() {
		return this.size === 'medium';
	}
	@HostBinding("class.rbc-input-large") get inputSizeLargeClass() {
		return this.size === 'large';
	}
	@HostBinding("class.rbc-input-width-small") get inputSizeWidthSmallClass() {
		return this.sizeWidth === 'small';
	}
	@HostBinding("class.rbc-input-width-medium") get inputSizeWidthMediumClass() {
		return this.sizeWidth === 'medium';
	}
	@HostBinding("class.rbc-input-width-large") get inputSizeWidthLargeClass() {
		return this.sizeWidth === 'large';
	}
	@HostBinding("class.rbc-input-width-auto") get inputSizeWidthAutoClass() {
		return this.sizeWidth === 'auto';
	}

	@HostBinding("attr.data-parent-id") get hostAttribute() {
		return this.id;
	}

	constructor(private idService: IdService, @Optional() @Self() public controlDirective: NgControl) {
		this.id = this.id ? this.id : this.idService.generate();
		if(this.controlDirective){
			controlDirective.valueAccessor = this;
		}
	}

	setInvalid(){
		let flag;
		this.controlDirective ? flag = this.controlDirective.invalid : flag = this.ariaInvalid;
		return flag;
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
	}

	ngOnInit() {
		if (this.value === undefined) {
			this.value = "";
		}
	}

	onKeyUp(e) {
		// Emit if not enter or escape
		if (e.keyCode !== 13 && e.keyCode !== 27) {
			this.value = e.target.value;
			this.onInput.emit(this.value);
			this.writeValue(e.target.value);
			this.inputComputeStatus();
		}
	}

	// Function to call when the rating changes.
	onChange = (rating: number) => {};
	// Function to call when the input is touched (when a star is clicked).
	onTouched = () => { };

	// Allows Angular to update the model (rating).
	// Update the model and changes needed for the view here.
	writeValue(value): void {
		this.value = value;
	}
	// Allows Angular to register a function to call when the model (rating) changes.
	// Save the function as a property to call later here.
	registerOnChange(fn: (rating: number) => void): void {
		this.onChange = fn;
	}
	// Allows Angular to register a function to call when the input has been touched.
	// Save the function as a property to call later here.
	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}
	// Allows Angular to disable the input.
	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	clearInput(event) {
		event.stopPropagation();

		this.writeValue("");
		this.clear.emit("");
	}

	inputSearch() {
		if (typeof this.search === "function") {
			this.search(this.value);
		}
		this.inputTextUpdate.emit(this.value);
	}

	lang() {
		if (typeof this.formatLang !== "undefined") {
			return this.formatLang;
		} else if (typeof window["lang"] !== "undefined") {
			return window["lang"];
		} else {
			return "en";
		}
	}

	typeGen() {
		if (this.secure) return 'password';
		else if (this.formatNumberLive) return 'text';
		else return this.type;
	}

	typeCheck() {
		return this.statusType === "success" ||
			this.statusType === "warning" ||
			this.statusType === "danger" ||
			this.statusType === "info"
			? this.statusType
			: "info";
	}

	inputClassGen() {
		const newClass = {};
		newClass["rbc-input-icon-pr"] = this.typeGen() === "search";
		newClass[this.statusType] = true ? this.statusType : null;
		return newClass;
	}

	spanClassGen() {
		let classes = {};
		// input size
		switch (this.size) {
			case "small":
				classes["rbc-input-small"] = true;
				break;
			case "medium":
				classes["rbc-input-medium"] = true;
				break;
			case "large":
				classes["rbc-input-large"] = true;
		}

		return classes;

	}

	// classGen() {
	// 	let classes = "";

	// 	classes += " rbc-input";
	// 	classes += this.unit ? classes + " rbc-input-unit-wrap" : "";
	// 	classes += this.size ? classes + " rbc-input-" + this.size : "";
	// 	classes += this.sizeWidth ? classes + " rbc-input-width-" + this.sizeWidth : "";
	// 	classes += this.noWidth ? classes + " rbc-input-width-none" : "";
	// 	return classes;
	// }

	unitClassGen() {
		return {
			filled: this.value !== null && this.value !== "",
			disabled: this.disabled,
			[this.statusTypeComputed]: this.status
		};
	}

	replaceAndFormatNumberLive() {
		if (typeof this.value !== "undefined" && this.value !== "" &&  this.value != null) {
			let nbDecimal = this.value.toString().split(".").length > 1 ? this.value.toString().split(".")[1].length: 0;

			if (typeof this.lang() !== "undefined" && this.lang() === "fr") {
				let sanitizedNumber = this.value.toString().replace(/,/g, ".");
				nbDecimal = sanitizedNumber.split(".").length > 1 ? sanitizedNumber.split(".")[1].length : 0;
				this.value = formatNumber(sanitizedNumber,"fr",nbDecimal);
			} else {
				let sanitizedNumber = this.value.toString().replace(/,/g, "");
				this.value = formatNumber(sanitizedNumber,"en",nbDecimal);
			}
		}
	}

	checkForErrors(val: string | number) {
		if (this.validateRegex && this.validateRegex !== "") {
			const regex = new RegExp(this.validateRegex);
			return !regex.test(val.toString());
		} else {
			return false;
		}
	}

	describedbyGen() {
		if (
			this.status ||
			this.helpText ||
			this.ariaDescribedBy ||
			typeof this.unit !== "undefined"
		) {
			const descriptors = {
				[this.id + "Status"]: this.status,
				[this.id + "HelpText"]: this.helpText,
				[this.ariaDescribedBy]: this.ariaDescribedBy,
				[this.id + "Unit"]: typeof this.unit !== "undefined"
			};
			return Object.keys(descriptors)
				.filter(key => descriptors[key])
				.join(" ");
		} else {
			return;
		}
	}

	onFocus(e) {
			if (this.formatNumberLive) {
				if (typeof this.value != 'undefined' && this.value != '' && this.value != null) {
					if (typeof this.lang != 'undefined' && this.lang() == 'fr') {
						this.value = Number(this.value.toString().replace(/[\xa0\u202f]/g, '').replace(/,/g,'.'));
					} else {
						this.value = Number(this.value.toString().replace(/,/g, ''));
					}
				}
				if (navigator.userAgent.toLowerCase().indexOf('firefox') == -1) {
					// Changing type dynamically in Firefox creates a blur event and then loose the ability to focus in the input.
					this.typeOverwrite = 'number';
				}
			}
		this.focusIn.emit(this.value);
	}

	onFocusOut(event) {
		// event.stopPropagation();
		this.focused = true;
		if (this.formatNumberLive) {
			this.typeOverwrite = false;
			if (this.value !== "") {
				setTimeout(() => {
					this.replaceAndFormatNumberLive();
				});
				this.typeOverwrite = "text";
			}
			this.writeValue(this.value);
		}
		this.focusOut.emit(this.value);
		this.blur.emit(event)
		this.onTouched();
	}

	inputComputeStatus() {
		if (this.validateRegex && this.status) {
			const regex = new RegExp(this.validateRegex);
			const isInvalid = !regex.test(this.value);
			this.statusTypeComputed = isInvalid ? "danger" : this.statusType;
			this.statusMessage = isInvalid ? this.status : "";
			this.statusUpdate.emit(isInvalid);
		} else if (this.validateHtml && this.status !== false) {
			const myInput = document.getElementById(this.id) as HTMLInputElement;
			if (myInput) {
				const isInvalid = !myInput.checkValidity()
				this.statusTypeComputed = isInvalid ? "danger" : this.statusType;
				this.statusMessage = isInvalid && this.status === true ? myInput.validationMessage : isInvalid && this.status !== true ? this.status : "";
				this.statusUpdate.emit(isInvalid);
			}

		} else {
			this.statusTypeComputed = this.statusType;
			this.statusMessage = this.status;
		}
	}
}
